/**
 * Чтение `m.relates_to` события — ОДНО место на весь клиент.
 *
 * ПОЧЕМУ НЕ `ev.getRelation()` (баг Антона, 2026-09-21 10:11: «цитаты в
 * ответах не работают ВООБЩЕ»).
 *
 * `getRelation()` в matrix-js-sdk 42.3.0 (models/event.js:1238-1257) сначала
 * зовёт `isRelation()`, а тот требует `rel_type` И `event_id` НА ВЕРХНЕМ
 * УРОВНЕ `m.relates_to`:
 *
 *     return !!(relation?.rel_type && relation.event_id && …);
 *
 * Обычный rich reply по спеке («Rich replies») выглядит иначе — там ровно
 * одно вложенное поле и НЕТ ни `rel_type`, ни верхнего `event_id`:
 *
 *     "m.relates_to": { "m.in_reply_to": { "event_id": "$…" } }
 *
 * Значит для КАЖДОГО обычного реплая `isRelation()` = false и
 * `getRelation()` = null. Замер на настоящем SDK (.probe-reply, 2026-09-21):
 * `isRelation=false`, `getRelation=null`, при этом
 * `getWireContent()["m.relates_to"]` на месте. Цитата не строилась никогда —
 * дефект не связан ни с удалением, ни с надгробиями.
 *
 * ПОЧЕМУ `getWireContent()` ПЕРВЫМ. В зашифрованной комнате `m.relates_to`
 * ВЫНЕСЕН из шифруемого тела наружу — он лежит в wire content (то же самое
 * пишет сам SDK в комментарии к `isRelation`). `getContent()` у
 * расшифрованного события отдаёт clearEvent.content, где связи нет вовсе.
 * Поэтому: сначала wire, потом clear как запас (незашифрованные комнаты,
 * локальное эхо до шифрования, клиенты, положившие связь только внутрь).
 *
 * `getRelation()` остаётся правильным инструментом там, где `rel_type`
 * обязателен по определению — `m.replace`, `m.annotation`, `m.thread`
 * (см. isOrphanedRelation и сборку реакций в timeline.ts). Их НЕ трогаем.
 */
import type { MatrixEvent } from "matrix-js-sdk";

/** Разобранный `m.relates_to`. Поля необязательны — как в сети. */
export interface RelatesTo {
  rel_type?: string;
  event_id?: string;
  /** MSC3440: true — `m.in_reply_to` здесь лишь фолбэк для тредовой цепочки. */
  is_falling_back?: boolean;
  "m.in_reply_to"?: { event_id?: string };
}

/**
 * `m.relates_to` события или null.
 *
 * В отличие от `ev.getRelation()` НЕ требует `rel_type`/верхнего `event_id`,
 * поэтому видит обычные rich replies. Шифрованные комнаты покрыты через
 * `getWireContent()`.
 */
export function relatesToOf(ev: MatrixEvent): RelatesTo | null {
  const wire = ev.getWireContent()?.["m.relates_to"];
  if (wire && typeof wire === "object") return wire as RelatesTo;
  const clear = ev.getContent()?.["m.relates_to"];
  if (clear && typeof clear === "object") return clear as RelatesTo;
  return null;
}

/**
 * id сообщения, на которое это событие — ОТВЕТ (цитата), или null.
 *
 * ТРЕДЫ. Внутри треда `m.in_reply_to` встречается в двух ролях:
 *   - `rel_type: "m.thread"` + `is_falling_back: true` — это НЕ ответ, а
 *     совместимость со старыми клиентами: связь указывает на корень (или на
 *     последнее сообщение) только чтобы тред склеился в цепочку. Цитату
 *     рисовать нельзя — иначе каждая реплика в треде получит фальшивую
 *     цитату корня. Замер S6 показал ровно это до правки.
 *   - `is_falling_back: false` (или поля нет — по спеке это значение по
 *     умолчанию) — настоящий ответ на конкретное сообщение внутри треда.
 *     Цитата нужна.
 */
export function replyTargetId(ev: MatrixEvent): string | null {
  const rel = relatesToOf(ev);
  if (!rel) return null;
  if (rel.rel_type === "m.thread" && rel.is_falling_back === true) return null;
  const id = rel["m.in_reply_to"]?.event_id;
  return typeof id === "string" && id.length > 0 ? id : null;
}

/**
 * Срезать ТЕКСТОВУЮ fallback-цитату из `body` реплая.
 *
 * Спека («Rich replies», Fallbacks): отправитель ОБЯЗАН продублировать
 * оригинал в начале `body` строками `> <@user:server> текст`, а клиент,
 * который умеет рисовать цитату сам, ОБЯЗАН этот кусок убрать перед
 * показом. Мы его и шлём (roomActions.sendReply), и раньше же и
 * показывали — но это не бросалось в глаза, потому что цитаты не
 * рисовались вовсе. Как только `replyOf` заработал, тот же текст стал
 * виден ДВАЖДЫ: в цитате и в теле пузыря. Замер probe-bubble/probe-nested.
 *
 * Режем ТОЛЬКО ведущий блок `>`-строк и пустую строку за ним, и ТОЛЬКО у
 * события, которое реально является ответом. Обычное сообщение, начатое
 * человеком с «> », — это его цитата, её трогать нельзя.
 */
export function stripReplyFallback(body: string, isReply: boolean): string {
  if (!isReply || !body.startsWith(">")) return body;
  const lines = body.split("\n");
  let i = 0;
  while (i < lines.length && lines[i].startsWith(">")) i += 1;
  // За блоком цитаты по спеке идёт ровно одна пустая строка-разделитель.
  if (i < lines.length && lines[i].trim() === "") i += 1;
  const rest = lines.slice(i).join("\n");
  // Защита от кривого fallback: если после среза не осталось ничего,
  // показываем исходный текст — пустой пузырь хуже лишней цитаты.
  return rest.trim().length > 0 ? rest : body;
}

/**
 * Срезать HTML-цитату `<mx-reply>…</mx-reply>` из `formatted_body`.
 *
 * Та же спека, тот же довод, что и выше, только для форматированного тела
 * (наш sendReply кладёт туда `<mx-reply><blockquote>…`). Работаем строкой,
 * а не DOMParser: модуль общий для UI и уведомлений, а в уведомлениях
 * DOM недоступен. Тег по спеке нерекурсивный и стоит первым.
 */
export function stripFormattedReplyFallback(html: string, isReply: boolean): string {
  if (!isReply) return html;
  const end = html.toLowerCase().indexOf("</mx-reply>");
  if (end < 0) return html;
  const rest = html.slice(end + "</mx-reply>".length);
  return rest.trim().length > 0 ? rest : html;
}
